import {
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { FC, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { PIC } from "@ryde/assets";
import { MuiButton } from "@ryde/controls";
import { LOGS_COLUMN_KEY, useIsMobile } from "@ryde/shared";

import { RichLogsExportDropdownStyled } from "./rich-logs.export-dropdown.styled";
import { IExportLogsCsvData } from "./rich-logs.models";

interface IRichLogsExportDropdownProps {
  allLogsCsvData: IExportLogsCsvData[];
  filteredLogsCsvData: IExportLogsCsvData[];
  fileName: string;
}

export const RichLogsExportDropdown: FC<IRichLogsExportDropdownProps> = ({
  allLogsCsvData,
  filteredLogsCsvData,
  fileName,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const anchorRef = useRef<HTMLDivElement>(null);
  const csvWrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [csvData, setCsvData] = useState<IExportLogsCsvData[]>([]);

  const hasAnyExportData = Boolean(allLogsCsvData?.length);
  const hasFilteredExportData = Boolean(filteredLogsCsvData?.length);

  const closeMenu = useEvent((event?: MouseEvent | TouchEvent) => {
    if (event && anchorRef.current?.contains(event.target as Node)) {
      return;
    }
    setIsOpen(false);
  });

  const triggerDownload = useEvent((data: IExportLogsCsvData[]) => {
    if (!data.length) {
      return;
    }
    setCsvData(data);
    setTimeout(() => {
      csvWrapperRef.current?.querySelector("a")?.click();
    }, 0);
  });

  const handleToggleMenu = useEvent(() => {
    if (!hasAnyExportData) {
      return;
    }
    setIsOpen(previous => !previous);
  });

  const handleExportAll = useEvent(() => {
    triggerDownload(allLogsCsvData);
    closeMenu();
  });

  const handleExportFiltered = useEvent(() => {
    triggerDownload(filteredLogsCsvData);
    closeMenu();
  });

  return (
    <RichLogsExportDropdownStyled
      ref={anchorRef}
      className="view-logs-save-csv-logs-link"
    >
      <MuiButton
        disabled={!hasAnyExportData}
        muiVariant="outlined"
        muiSize="small"
        muiStyle="warning"
        onClick={handleToggleMenu}
        className="rich-logs-export-button"
      >
        {isMobile ? <PIC.DownloadSVG /> : t("general.EXPORT")}
        <ArrowDropDownIcon className="rich-logs-export-chevron" />
      </MuiButton>

      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        placement="bottom-end"
        className="rich-logs-export-popper"
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={8}>
              <ClickAwayListener onClickAway={closeMenu}>
                <MenuList autoFocusItem data-e2e="rich-logs-export-menu">
                  <MenuItem
                    disabled={!hasAnyExportData}
                    onClick={handleExportAll}
                  >
                    {t("general.EXPORT_LOGS_ALL_TO_CSV")}
                  </MenuItem>
                  <MenuItem
                    disabled={!hasFilteredExportData}
                    onClick={handleExportFiltered}
                  >
                    {t("general.EXPORT_LOGS_FILTERED_TO_CSV")}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <div ref={csvWrapperRef} className="rich-logs-export-csv-hidden">
        <CSVLink
          data={csvData}
          headers={[{ key: LOGS_COLUMN_KEY, label: LOGS_COLUMN_KEY }]}
          filename={fileName}
        />
      </div>
    </RichLogsExportDropdownStyled>
  );
};
